import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Download, ChevronRight, RefreshCw, ScrollText, UserMinus, UserPlus } from 'lucide-react';
import { useAllDTalkForPeople, useTeamDTalk, useDTalkLogExport, useDTalkAccess, useDTalkSyncAllToSheets, useDTalkAuditLog, useSetDTalkLicenseExclusion } from '@/hooks/usePerformance';
import { DTalkPeopleMetrics } from '@/components/performance/dtalk/DTalkPeopleMetrics';
import { DTalkAreaProgressChart } from '@/components/performance/dtalk/DTalkAreaProgressChart';
import { DTalkPeopleFilters, DTALK_FILTER_ALL, type DTalkFilterDef } from '@/components/performance/dtalk/DTalkPeopleFilters';
import { DTalkLicenseManagerDialog } from '@/components/performance/dtalk/DTalkLicenseManagerDialog';
import { downloadCsv } from '@/lib/csv';
import { DTalkCycleConfigPanel } from '@/components/performance/DTalkCycleConfigPanel';
import { DTalkHeader } from './DTalkLayout';
import { DTALK_STATUS, DTALK_DISPLAY_OPTIONS, dtalkChip } from '@/components/performance/dtalk/dtalkShared';
import { dtalkDisplayStatus } from '@/../../shared/dtalk-stats';
import { cn } from '@/lib/utils';
import { usePagination } from '@/hooks/usePagination';

/** En la vista de líder cada fila trae además dónde está en su árbol. */
type Row = NonNullable<ReturnType<typeof useAllDTalkForPeople>['data']>['rows'][number]
  & { isDirect?: boolean; depth?: number; directLeaderName?: string | null };

const EMPTY = DTALK_FILTER_ALL;
const PAGE_SIZE = 10;

function safeDateTime(ts: string): string {
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

const INITIAL_FILTERS: Record<string, string> = {
  area: EMPTY, team: EMPTY, manager: EMPTY, n1: EMPTY, country: EMPTY,
  level: EMPTY, role: EMPTY, year: EMPTY, alert: EMPTY, status: EMPTY, reach: EMPTY,
};

/**
 * `org` = Vista People (toda la base). `team` = la misma vista para cada líder
 * con TODO su árbol (Sol 20/10/2026), también con la ventana cerrada: KPIs,
 * avance por área, filtros, tabla e informe de cada uno; sin licencias, sin
 * panel de governance y con la exportación acotada a su equipo.
 */
export function DTalkPeopleTab({ scope = 'org' }: { scope?: 'org' | 'team' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isTeam = scope === 'team';
  const org = useAllDTalkForPeople(!isTeam);
  const team = useTeamDTalk(isTeam);
  const { data, isLoading } = isTeam ? team : org;
  const detailBase = `/performance/dtalk/${isTeam ? 'team' : 'people'}/`;
  const logExport = useDTalkLogExport();
  const { data: access } = useDTalkAccess();
  const isAdmin = !isTeam && !!access?.isGovernanceAdmin;
  // Licencias / elegibilidad: solo People (07/10/2026), y solo en su vista.
  const canManageEligibility = !isTeam && !!access?.canManageEligibility;
  const canExport = isTeam ? !!access?.canExport : true;
  const syncSheets = useDTalkSyncAllToSheets();
  const setExclusion = useSetDTalkLicenseExclusion();
  const [showAudit, setShowAudit] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);
  const audit = useDTalkAuditLog(isAdmin && showAudit);

  const rows: Row[] = (data?.rows ?? []) as Row[];
  const hasIndirects = isTeam && rows.some(r => r.isDirect === false);
  const [search, setSearch] = useState('');
  const [f, setF] = useState<Record<string, string>>(INITIAL_FILTERS);
  const set = (k: string, v: string) => setF(prev => ({ ...prev, [k]: v }));

  const opts = useMemo(() => {
    const uniq = (fn: (r: Row) => string | null | undefined) => [...new Set(rows.map(fn).filter(Boolean) as string[])].sort();
    return {
      area: uniq(r => r.employeeArea), team: uniq(r => r.employeeTeam), manager: uniq(r => r.peopleManager),
      n1: uniq(r => r.employeeN1Manager), country: uniq(r => r.employeeCountry), level: uniq(r => r.impactLevel),
      role: uniq(r => r.employeeRole),
      year: uniq(r => r.startDate ? r.startDate.slice(0, 4) : undefined),
    };
  }, [rows]);

  const matches = (r: Row, skipArea = false) => {
    const s = search.trim().toLowerCase();
    if (s && !`${r.employeeName} ${r.employeeLastName} ${r.employeeEmail}`.toLowerCase().includes(s)) return false;
    if (!skipArea && f.area !== EMPTY && r.employeeArea !== f.area) return false;
    if (f.team !== EMPTY && r.employeeTeam !== f.team) return false;
    if (f.manager !== EMPTY && r.peopleManager !== f.manager) return false;
    if (f.n1 !== EMPTY && r.employeeN1Manager !== f.n1) return false;
    if (f.country !== EMPTY && r.employeeCountry !== f.country) return false;
    if (f.level !== EMPTY && r.impactLevel !== f.level) return false;
    if (f.role !== EMPTY && r.employeeRole !== f.role) return false;
    if (f.year !== EMPTY && (r.startDate ?? '').slice(0, 4) !== f.year) return false;
    if (f.alert !== EMPTY && String(r.alerta) !== f.alert) return false;
    if (f.status !== EMPTY && dtalkDisplayStatus(r.processStatus) !== f.status) return false;
    if (f.reach === 'direct' && r.isDirect === false) return false;
    return true;
  };

  const filtered = useMemo(() => rows.filter(r => matches(r)), [rows, search, f]); // eslint-disable-line react-hooks/exhaustive-deps
  // El resumen por área compara TODAS las áreas: ignora el filtro de área
  // (colapsaría a una sola barra) pero respeta país/líder/level/etc.
  const filteredExceptArea = useMemo(() => rows.filter(r => matches(r, true)), [rows, search, f]); // eslint-disable-line react-hooks/exhaustive-deps

  const { pageItems: pageRows, footer: paginationFooter } = usePagination(filtered, PAGE_SIZE, JSON.stringify([search, f]));

  function handleExport() {
    const headers = ['Mail', 'Nombre', 'Apellido', 'Level', 'Equipo', 'Área', 'País', 'People Manager', 'N+1', 'Estado', 'Elegibilidad', 'Aspiración principal', 'Aspiraciones secundarias', 'Motivaciones', 'Competencias', 'Responsabilidades', 'Alerta', 'Motivos alerta', 'Claridad', 'Conclusión validada', 'Versión', 'Última actualización', 'Validación'];
    const csvRows = filtered.map(r => [
      r.employeeEmail, r.employeeName, r.employeeLastName, r.impactLevel ?? '', r.employeeTeam ?? '', r.employeeArea ?? '', r.employeeCountry ?? '',
      r.peopleManager ?? '', r.employeeN1Manager ?? '', t(dtalkChip(r.processStatus, r.eligibilityStatus).labelKey),
      r.eligibilityStatus, r.aspiracionPrincipal ?? '', (r.aspiracionesSecundarias ?? []).join('; '), (r.motivaciones ?? []).join('; '),
      (r.competenciasADesarrollar ?? []).join('; '), (r.responsabilidadesInteres ?? []).join('; '), r.alerta ? 'Sí' : 'No', (r.motivosAlerta ?? []).join('; '),
      r.claridad ?? '', r.conclusionValidada ?? '', String(r.versionNumber ?? 0), r.updatedAt ?? '', r.validatedAt ?? '',
    ]);
    downloadCsv(`dtalk-${isTeam ? 'mi-equipo-' : ''}${data?.cycle?.cycleLabel ?? 'ciclo'}.csv`, headers, csvRows);
    logExport.mutate({ scope: isTeam ? 'team' : 'org', rowCount: filtered.length });
  }

  if (isLoading) return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;

  const FILTERS: DTalkFilterDef[] = [
    ...(hasIndirects ? [{
      k: 'reach', label: t('performance.dtalk.leader.f_reach'),
      options: [{ value: 'direct', label: t('performance.dtalk.leader.reach_direct') }],
    }] : []),
    { k: 'area', label: t('performance.dtalkpeopletab.area'), options: opts.area.map(v => ({ value: v, label: v })) },
    { k: 'country', label: t('performance.dtalkpeopletab.pais'), options: opts.country.map(v => ({ value: v, label: v })) },
    { k: 'manager', label: t('performance.dtalkpeopletab.lider'), options: opts.manager.map(v => ({ value: v, label: v })) },
    { k: 'level', label: t('performance.dtalkpeopletab.impact_level'), options: opts.level.map(v => ({ value: v, label: v })) },
    { k: 'role', label: 'Rol', options: opts.role.map(v => ({ value: v, label: v })) },
    { k: 'team', label: 'Equipo', options: opts.team.map(v => ({ value: v, label: v })) },
    { k: 'n1', label: 'N+1', options: opts.n1.map(v => ({ value: v, label: v })) },
    { k: 'year', label: t('performance.dtalkpeopletab.ano_de_ingreso'), options: opts.year.map(v => ({ value: v, label: v })) },
    { k: 'alert', label: 'Alerta', options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }] },
    {
      k: 'status', label: 'Estado',
      options: DTALK_DISPLAY_OPTIONS.map(s => ({ value: s, label: t(DTALK_STATUS[s].labelKey) })),
    },
  ];

  return (
    <div className="space-y-5">
      <DTalkHeader title={t(isTeam ? 'performance.dtalk.leader.title' : 'performance.dtalk.people.title')} cycleLabel={data?.cycle?.cycleLabel} />

      {isTeam && rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">{t('performance.dtalk.leader.no_reports')}</CardContent></Card>
      ) : (<>

      {/* Filtros arriba (mockup): las métricas se recalculan con lo filtrado */}
      <DTalkPeopleFilters
        search={search}
        onSearch={setSearch}
        filters={FILTERS}
        values={f}
        onSet={set}
        onClearAll={() => { setF(INITIAL_FILTERS); setSearch(''); }}
      />

      {/* Métricas del proceso — se recalculan con los filtros aplicados */}
      <DTalkPeopleMetrics
        rows={filtered}
        canEditLicense={canManageEligibility}
        onOpenLicenses={() => setShowLicenses(true)}
      />

      {/* Avance comparativo por área (ignora el filtro de área, respeta el resto) */}
      <DTalkAreaProgressChart rows={filteredExceptArea} />

      {/* Panel admin (governance): sync a Sheets + registro de actividad (§6.3/§15/§17) */}
      {isAdmin && (
        <Card><CardContent className="py-3 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" disabled={syncSheets.isPending} onClick={() => syncSheets.mutate()}>
                <RefreshCw className={cn('h-4 w-4', syncSheets.isPending && 'animate-spin')} />
                {t('performance.dtalk.people.admin_sync', { defaultValue: 'Sincronizar a Sheets' })}
              </Button>
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setShowAudit(v => !v)}>
                <ScrollText className="h-4 w-4" />{t('performance.dtalk.people.audit_title', { defaultValue: 'Registro de actividad' })}
              </Button>
            </div>
            {access?.sheetsConfigured === false && (
              <span className="text-xs text-muted-foreground">{t('performance.dtalk.people.sheets_off', { defaultValue: 'Google Sheets no configurado' })}</span>
            )}
          </div>
          <DTalkCycleConfigPanel />
          {showAudit && (
            <div className="max-h-56 overflow-y-auto rounded-lg border divide-y text-xs">
              {(audit.data ?? []).length === 0 && <p className="p-3 text-muted-foreground">{t('performance.dtalk.people.audit_empty', { defaultValue: 'Sin actividad registrada.' })}</p>}
              {(audit.data ?? []).map((e, i) => (
                <div key={i} className="p-2 flex items-center gap-2">
                  <span className={cn('inline-flex rounded px-1.5 py-0.5 font-semibold',
                    e.kind === 'export' ? 'bg-blue-100 text-blue-700' : e.kind === 'modification' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600')}>{e.kind}</span>
                  <span className="text-muted-foreground">{safeDateTime(e.at)}</span>
                  <span className="truncate"><b>{e.actor}</b> → {e.target}{e.detail ? ` · ${e.detail}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Base consolidada — paginada (mockup): ya no es un scroll eterno */}
      <Card><CardContent className="pt-5">
        <p className="font-bold mb-3">{t('performance.dtalk.people.base')}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                <th className="py-2 pr-3 font-semibold">{t('performance.dtalk.people.col_name')}</th>
                <th className="py-2 pr-3 font-semibold hidden sm:table-cell">{t('performance.dtalk.people.col_area')}</th>
                {hasIndirects && <th className="py-2 pr-3 font-semibold hidden md:table-cell">{t('performance.dtalk.leader.col_direct_leader')}</th>}
                <th className="py-2 pr-3 font-semibold">{t('performance.dtalk.people.col_aspiration')}</th>
                <th className="py-2 pr-3 font-semibold">{t('performance.dtalk.people.col_alert')}</th>
                <th className="py-2 pr-3 font-semibold">{t('performance.dtalk.people.col_status')}</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(r => {
                const st = dtalkChip(r.processStatus, r.eligibilityStatus);
                return (
                  <tr key={r.employeeEmail} className="border-b last:border-0 hover:bg-accent/40 cursor-pointer" onClick={() => navigate(`${detailBase}${encodeURIComponent(r.employeeEmail)}`)}>
                    <td className="py-2.5 pr-3 font-semibold">{r.employeeName} {r.employeeLastName}</td>
                    <td className="py-2.5 pr-3 text-muted-foreground hidden sm:table-cell">{r.employeeArea || '—'}</td>
                    {hasIndirects && <td className="py-2.5 pr-3 text-muted-foreground hidden md:table-cell">{r.isDirect === false ? (r.directLeaderName || '—') : t('performance.dtalk.leader.direct_report')}</td>}
                    <td className="py-2.5 pr-3 text-muted-foreground">{r.aspiracionPrincipal || '—'}</td>
                    <td className="py-2.5 pr-3"><span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-semibold', r.alerta ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700')}>{r.alerta ? t('performance.dtalk.leader.alert_yes') : t('performance.dtalk.leader.alert_no')}</span></td>
                    <td className="py-2.5 pr-3"><span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-semibold', st.badge)}>{t(st.labelKey)}</span></td>
                    <td className="py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {/* People marca/desmarca licencia: la persona sale del
                          denominador del % de avance mientras esté marcada */}
                      {canManageEligibility && <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-xs"
                        disabled={setExclusion.isPending}
                        title={r.eligibilityStatus === 'license' ? t('performance.dtalkpeopletab.reactivar_dtalk') : t('performance.dtalkpeopletab.marcar_en_licencia')}
                        onClick={() => setExclusion.mutate({ employeeEmail: r.employeeEmail, excluded: r.eligibilityStatus !== 'license' })}
                      >
                        {r.eligibilityStatus === 'license' ? <UserPlus className="h-3.5 w-3.5" /> : <UserMinus className="h-3.5 w-3.5" />}
                      </Button>}
                      <ChevronRight
                        className="ml-1 inline h-4 w-4 cursor-pointer text-muted-foreground"
                        onClick={() => navigate(`${detailBase}${encodeURIComponent(r.employeeEmail)}`)}
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={hasIndirects ? 7 : 6} className="py-8 text-center text-sm text-muted-foreground">{t('performance.dtalk.people.no_records')}</td></tr>}
            </tbody>
          </table>
        </div>
        {paginationFooter}
      </CardContent></Card>

      {/* Exportación (el líder: solo su equipo, queda en el registro) */}
      {canExport && <Card><CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-bold">{t('performance.dtalk.people.export')}</p>
          <p className="text-sm text-muted-foreground">{t('performance.dtalk.people.export_hint')}</p>
        </div>
        <Button onClick={handleExport} disabled={logExport.isPending} className="gap-2 bg-foreground text-background hover:bg-foreground/90"><Download className="h-4 w-4" />{t('performance.dtalk.people.export_csv')}</Button>
      </CardContent></Card>}
      </>)}

      {/* Gestión de licencias (dialog): opera sobre TODAS las filas */}
      <DTalkLicenseManagerDialog
        open={showLicenses && canManageEligibility}
        onOpenChange={setShowLicenses}
        rows={rows}
        pending={setExclusion.isPending}
        onToggleLicense={(employeeEmail, excluded) => setExclusion.mutate({ employeeEmail, excluded })}
      />
    </div>
  );
}
